import React, {lazy, FC, Suspense} from 'react'
import {Route, Routes, Navigate, useLocation} from 'react-router-dom'
import TopBarProgress from 'react-topbar-progress-indicator'
import {DashboardWrapper} from '../modules/user/pages/dashboard/DashboardWrapper'
import {getCSSVariableValue} from '../../_metronic/assets/ts/_utils'
import {WithChildren} from '../../_metronic/helpers'
import BuilderPageWrapper from '../modules/user/pages/layout-builder/BuilderPageWrapper'
import {useReduxAuth} from '../modules/auth/useReduxAuth'
import UnauthorizedPage from '../modules/errors/UnauthorizedPage'
import DoctorListingPage from '../modules/user/pages/doctor/DoctorListingPage'
import NurseListingPage from '../modules/user/pages/nurse/NurseListingPage'
import PatientListingPage from '../modules/user/pages/patient/PatientListingPage'
import DoctorDetailsPage from '../modules/user/pages/doctor/DoctorDetailsPage'
import NurseDetailsPage from '../modules/user/pages/nurse/NurseDetailsPage'
import PatientDetailsPage from '../modules/user/pages/patient/PatientDetailsPage'
import MedicalAssistantListingPage from '../modules/user/pages/medical-assistant/MedicalAssistantListingPage'
import MedicalAssistantDetailsPage from '../modules/user/pages/medical-assistant/MedicalAssistantDetailsPage'
import AllAvailableDevicesListingPage from '../modules/user/pages/device/AllAvailableDevicesListingPage'
import RequestedDeviceListing from '../modules/user/pages/device/RequestedDeviceListing'
import DoctorDeviceListingPage from '../modules/user/pages/device/DoctorDeviceListingPage'
import TimeTrackedListingPage from '../modules/user/pages/time-tracked/TimeTrackedListingPage'
import NotificationsPage from '../modules/user/pages/notifications/NotificationsPage'
import CriticalitySettingsPage from '../modules/user/pages/criticality-settings/CriticalitySettingsPage'
import PaymentSuccess from '../modules/user/pages/stripe-payments/PaymentSuccess'
import HealthCriticalityAlerts from '../modules/user/pages/criticality-settings/HealthCriticalityAlerts'
import PaymentHistory from '../modules/user/pages/payments/PaymentHistory'
import BillingReport from '../modules/user/pages/billing-report/BillingReport'
import AdminBillingReport from '../modules/user/pages/billing-report/AdminBillingReport'
import DoctorRequestedDevices from '../modules/user/pages/device/DoctorRequestedDevices'
import SubscriptionPage from '../modules/user/pages/stripe-payments/SubscriptionPage'
import AdminLayout from '../../_metronic/layout/AdminLayout'
import RoutesRoleGuard from '../modules/user/components/permissions/RoutesPermissionGuard'
import {USER_ROLES} from '../constants/StatusConstants'
import BPAlertSettings from '../modules/user/pages/bp-alert-settings/BPAlertSettings'
import SubAdminListingPage from '../modules/user/pages/sub-admin/SubAdminListingPage'
import SubAdminDetailsPage from '../modules/user/pages/sub-admin/SubAdminDetailsPage'
import AdminDashboardWrapper from '../modules/user/pages/admin-dashboard/AdminDashboardWrapper'
import ManageCards from '../modules/user/pages/stripe-payments/ManageCards'
import Settings from '../modules/user/pages/settings/Settings'
import BillingCycleEndingReport from '../modules/user/pages/billing-report/BillingCycleEndingReport'
import DemoVideosPage from '../modules/user/pages/demo/DemoVideosPage'

const ProfilePage = lazy(() => import('../modules/user/modules/profile/ProfilePage'))
const AccountPage = lazy(() => import('../modules/user/modules/accounts/AccountPage'))

const UserRoutes = () => {
  const {user: currentUser} = useReduxAuth()

  // if getting dashboard
  const location = useLocation()
  const authRoutes = [
    '/login',
    '/reset-password',
    '/forgot-password',
    'set-password',
    '/email-confirmation',
  ]
  const isAuthRoute = authRoutes.some((route) => location.pathname.startsWith(route))

  return (
    <Routes>
      <Route element={<AdminLayout />}>
        {' '}
        {/* Redirect to Dashboard */}+ {/* Redirect based on role */}
        <Route
          index
          element={
            currentUser?.role_id === USER_ROLES.BILLING_PERSONNEL ? (
              <Navigate to='/billing-report' replace />
            ) : (
              <Navigate to='/dashboard' replace />
            )
          }
        />{' '}
        {/* Dashboard (Accessible to All Logged-in Users) */}
        <Route
          path='dashboard'
          element={
            currentUser?.role_id === USER_ROLES.BILLING_PERSONNEL ? (
              <Navigate to='/billing-report' replace />
            ) : currentUser?.role_id === USER_ROLES.ADMIN ? (
              <AdminDashboardWrapper />
            ) : (
              <DashboardWrapper />
            )
          }
        />
        {/* Admin Dashboard (Admin Only) */}
        <Route
          path='admin-dashboard'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN]}>
              <Suspense fallback={<TopBarProgress />}>
                <AdminDashboardWrapper />
              </Suspense>
            </RoutesRoleGuard>
          }
        />
        {/* Unauthorized Page */}
        <Route path='unauthorized' element={<UnauthorizedPage />} />
        <Route path='builder' element={<BuilderPageWrapper />} />
        {/* Protected Routes with PermissionGuard */}
        {/* profile listing */}
        <Route
          path='/profile/*'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.BILLING_PERSONNEL,
                USER_ROLES.MEDICAL_ASSISTANT,
              ]}
            >
              <SuspensedView>
                <ProfilePage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* doctor listing */}
        <Route
          path='/doctor'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
              ]}
            >
              <SuspensedView>
                <DoctorListingPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* sub-admin listing page */}
        <Route
          path='/sub-admin'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN]}>
              <SuspensedView>
                <SubAdminListingPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* nurse listing */}
        <Route
          path='/staff'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
                USER_ROLES.MEDICAL_ASSISTANT,
              ]}
            >
              <SuspensedView>
                <NurseListingPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* medical assistant listing */}
        <Route
          path='/medical-assistant'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN]}>
              <SuspensedView>
                <MedicalAssistantListingPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* patient listing */}
        <Route
          path='/patient'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
                USER_ROLES.MEDICAL_ASSISTANT,
              ]}
            >
              <SuspensedView>
                <PatientListingPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* time-tracked listing */}
        <Route
          path='/time-tracked'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
              <SuspensedView>
                <TimeTrackedListingPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* device listing */}
        <Route
          path='/my/devices'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
              <SuspensedView>
                <DoctorDeviceListingPage userData={currentUser} />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Listing of All Device Available for the Admin */}
        <Route
          path='/list/devices'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}>
              <SuspensedView>
                <AllAvailableDevicesListingPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Listing of All Device Requests By Doctor */}
        <Route
          path='/requested/devices'
          element={
            <RoutesRoleGuard
              allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.DOCTOR, USER_ROLES.SUB_ADMIN]}
            >
              <SuspensedView>
                <RequestedDeviceListing />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* doctor details */}
        <Route
          path='/doctor-details/:doctorId/*'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
                USER_ROLES.MEDICAL_ASSISTANT,
              ]}
            >
              <SuspensedView>
                <DoctorDetailsPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        <Route
          path='/sub-admin-details/:subAdminId/*'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN]}>
              <SuspensedView>
                <SubAdminDetailsPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* medical assistant details */}
        <Route
          path='/medical-assistant-details/:medicalAssistantId/*'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}>
              <SuspensedView>
                <MedicalAssistantDetailsPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* nurse details */}
        <Route
          path='/nurse-details/:nurseId/*'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
                USER_ROLES.MEDICAL_ASSISTANT,
              ]}
            >
              <SuspensedView>
                <NurseDetailsPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* patient details */}
        <Route
          path='/patient-details/:patientId/*'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
                USER_ROLES.MEDICAL_ASSISTANT,
              ]}
            >
              <SuspensedView>
                <PatientDetailsPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Criticality Settings listing */}
        <Route
          path='/notifications'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.ADMIN, USER_ROLES.MEDICAL_ASSISTANT]}>
              <SuspensedView>
                <NotificationsPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        <Route
          path='/settings/alerts'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
              <SuspensedView>
                <BPAlertSettings />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        <Route
          path='/health-alerts'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
              <SuspensedView>
                <HealthCriticalityAlerts />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        <Route
          path='/health-alerts/settings'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.MEDICAL_ASSISTANT]}>
              <SuspensedView>
                <CriticalitySettingsPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* payment success route */}
        <Route
          path='/payment-success'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.ADMIN]}>
              <SuspensedView>
                <PaymentSuccess />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Payment Cancelled Route */}
        <Route
          path='/payment-cancelled'
          element={
            <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.ADMIN]}>
              <SuspensedView>{/* <PaymentCancelled /> */}</SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* payment history route */}
        <Route
          path='/payment-history'
          element={
            <RoutesRoleGuard
              allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}
            >
              <SuspensedView>
                <PaymentHistory />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Subscription Route */}
        <>
          <Route
            path='/subscription'
            element={
              <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR]}>
                <SuspensedView>
                  <SubscriptionPage />
                </SuspensedView>
              </RoutesRoleGuard>
            }
          />

          <Route
            path='/manage-cards'
            element={
              <RoutesRoleGuard allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE]}>
                <SuspensedView>
                  <ManageCards />
                </SuspensedView>
              </RoutesRoleGuard>
            }
          />
        </>
        {/* Billing Report */}
        <Route
          path='/billing-report'
          element={
            <RoutesRoleGuard
              allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]}
            >
              <SuspensedView>
                <BillingReport />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Admin Billing Report */}
        <Route
          path='/admin/billing-report'
          element={
            <RoutesRoleGuard
              allowedRoles={[USER_ROLES.ADMIN, USER_ROLES.SUB_ADMIN]}
            >
              <SuspensedView>
                <AdminBillingReport />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Billing Report */}
        <Route
          path='/billing-cycle-ending-report'
          element={
            <RoutesRoleGuard
              allowedRoles={[USER_ROLES.DOCTOR, USER_ROLES.NURSE, USER_ROLES.BILLING_PERSONNEL, USER_ROLES.MEDICAL_ASSISTANT]}
            >
              <SuspensedView>
                <BillingCycleEndingReport />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* Devices Requested by Doctor for doctor and staff/nurse only */}
        <Route
          path='/devices-requested'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
              ]}
            >
              <SuspensedView>
                <DoctorRequestedDevices />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        <Route
          path='account/*'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
                USER_ROLES.BILLING_PERSONNEL,
                USER_ROLES.MEDICAL_ASSISTANT,
              ]}
            >
              <SuspensedView>
                <AccountPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        <Route
          path='/settings'
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.MEDICAL_ASSISTANT,
                // USER_ROLES.ADMIN,
                // USER_ROLES.SUB_ADMIN,
              ]}
            >
              <SuspensedView>
                <Settings />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        <Route
          path='/demos' 
          element={
            <RoutesRoleGuard
              allowedRoles={[
                USER_ROLES.DOCTOR,
                USER_ROLES.NURSE,
                USER_ROLES.ADMIN,
                USER_ROLES.SUB_ADMIN,
              ]}
            >
              <SuspensedView>
                <DemoVideosPage />
              </SuspensedView>
            </RoutesRoleGuard>
          }
        />
        {/* if logged in and trigger any auth route then redirect user to dashboard */}
        {isAuthRoute && (
          <Route path={location.pathname} element={<Navigate to='/dashboard' replace />} />
        )}
        {/* Redirect Invalid Routes to 404 */}
        <Route path='*' element={<Navigate to='/error/404' />} />
      </Route>
    </Routes>
  )
}

const SuspensedView: FC<WithChildren> = ({children}) => {
  const baseColor = getCSSVariableValue('--bs-primary')
  TopBarProgress.config({
    barColors: {'0': baseColor},
    barThickness: 1,
    shadowBlur: 5,
  })
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>
}

export {UserRoutes}
